import { useState } from 'react';
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  Users,
  Building2,
  CreditCard,
  Shield,
  Bell,
  Zap,
  Database,
  Key,
  Globe,
  Terminal,
  ExternalLink,
  Copy,
  Check,
  Eye,
  Mail,
  Tag,
  MessageSquare,
  UserPlus,
  UserCheck,
  Lock
} from 'lucide-react';

interface GuideSection {
  id: string;
  title: string;
  icon: React.ElementType;
  color: string;
  content: React.ReactNode;
}

export default function AdminGuidePage() {
  const [expandedSection, setExpandedSection] = useState<string | null>('getting-started');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const CodeBlock = ({ code, id }: { code: string; id: string }) => (
    <div className="relative mt-3 mb-4">
      <pre className="bg-stone-900 text-stone-100 rounded-xl p-4 overflow-x-auto text-sm font-mono">
        {code}
      </pre>
      <button
        onClick={() => copyToClipboard(code, id)}
        className="absolute top-3 right-3 p-2 rounded-lg bg-stone-800 hover:bg-stone-700 text-stone-400 hover:text-stone-200 transition-colors"
      >
        {copiedCode === id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );

  const sections: GuideSection[] = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: BookOpen,
      color: 'bg-sage',
      content: (
        <div className="space-y-4">
          <p className="text-stone-600 dark:text-stone-400">
            Welcome to the SereneTouch Admin Portal. This guide will help you manage the platform effectively.
          </p>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200">Quick Links</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <a href="https://serenetouch.app" target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors">
              <Globe className="w-5 h-5 text-sage" />
              <span className="text-stone-700 dark:text-stone-300">Client App</span>
              <ExternalLink className="w-4 h-4 ml-auto text-stone-400" />
            </a>
            <a href="https://api.serenetouch.app" target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors">
              <Terminal className="w-5 h-5 text-blue-500" />
              <span className="text-stone-700 dark:text-stone-300">API</span>
              <ExternalLink className="w-4 h-4 ml-auto text-stone-400" />
            </a>
            <a href="https://dashboard.clerk.com" target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors">
              <Shield className="w-5 h-5 text-purple-500" />
              <span className="text-stone-700 dark:text-stone-300">Clerk Dashboard</span>
              <ExternalLink className="w-4 h-4 ml-auto text-stone-400" />
            </a>
            <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl hover:bg-stone-100 dark:hover:bg-stone-700 transition-colors">
              <CreditCard className="w-5 h-5 text-indigo-500" />
              <span className="text-stone-700 dark:text-stone-300">Stripe Dashboard</span>
              <ExternalLink className="w-4 h-4 ml-auto text-stone-400" />
            </a>
          </div>
        </div>
      ),
    },
    {
      id: 'user-management',
      title: 'User Management',
      icon: Users,
      color: 'bg-blue-500',
      content: (
        <div className="space-y-4">
          <p className="text-stone-600 dark:text-stone-400">
            Manage platform users and their access levels through Clerk's public metadata.
          </p>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200">Granting Admin Access</h4>
          <ol className="list-decimal list-inside space-y-2 text-stone-600 dark:text-stone-400">
            <li>Go to <strong>Clerk Dashboard → Users</strong></li>
            <li>Select the user you want to make an admin</li>
            <li>Scroll to <strong>Public Metadata</strong></li>
            <li>Add the following JSON:</li>
          </ol>
          <CodeBlock code={`{ "role": "admin" }`} id="admin-role" />

          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
            <p className="text-amber-800 dark:text-amber-200 text-sm">
              <strong>Important:</strong> Users must sign out and sign back in for role changes to take effect.
            </p>
          </div>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">User Roles</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-medium rounded">admin</span>
              <span className="text-stone-600 dark:text-stone-400">Full platform access, can view all studios and users</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium rounded">user</span>
              <span className="text-stone-600 dark:text-stone-400">Default role, studio owner with access to their own data</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'inviting-users',
      title: 'Inviting Team Members',
      icon: UserCheck,
      color: 'bg-teal-500',
      content: (
        <div className="space-y-4">
          <p className="text-stone-600 dark:text-stone-400">
            The admin portal is invite-only. Use the invite system to grant access to team members.
          </p>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200">How to Invite</h4>
          <ol className="list-decimal list-inside space-y-2 text-stone-600 dark:text-stone-400">
            <li>Go to the <strong>Users</strong> page</li>
            <li>Click <strong>Invite User</strong></li>
            <li>Enter their email address</li>
            <li>Select their role (<code>admin</code> or <code>marketing</code>)</li>
            <li>They receive an email invitation from Clerk</li>
            <li>They click the link, create their account, and have immediate access</li>
          </ol>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Available Roles</h4>
          <ul className="list-disc list-inside space-y-1 text-stone-600 dark:text-stone-400 ml-2">
            <li><code>admin</code> - Full access to all features</li>
            <li><code>marketing</code> - Access to Leads and Playbook only</li>
          </ul>

          <div className="p-4 bg-teal-50 dark:bg-teal-900/20 rounded-xl border border-teal-200 dark:border-teal-800">
            <p className="text-teal-800 dark:text-teal-200 text-sm">
              <strong>Note:</strong> The invitation sets their role automatically. They don't need to wait for approval - once they accept the invite and create their account, they have access.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'role-permissions',
      title: 'Role Permissions',
      icon: Lock,
      color: 'bg-violet-500',
      content: (
        <div className="space-y-4">
          <p className="text-stone-600 dark:text-stone-400">
            Control which pages each role can access. Permissions are stored in the database and can be configured through the Settings page.
          </p>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200">Configuring Permissions</h4>
          <ol className="list-decimal list-inside space-y-2 text-stone-600 dark:text-stone-400">
            <li>Go to <strong>Settings</strong> page</li>
            <li>Scroll to the <strong>Role Permissions</strong> section</li>
            <li>For each role, toggle the pages they should access</li>
            <li>Set the <strong>default page</strong> (where they land after login)</li>
            <li>Click <strong>Save Permissions</strong></li>
          </ol>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Available Pages</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {['Dashboard', 'Studios', 'Users', 'Subscriptions', 'Discounts', 'Leads', 'Analytics', 'Admin Guide', 'Growth Playbook', 'Settings'].map(page => (
              <div key={page} className="px-3 py-2 bg-stone-50 dark:bg-stone-800 rounded-lg text-sm text-stone-600 dark:text-stone-400">
                {page}
              </div>
            ))}
          </div>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Default Roles</h4>
          <div className="space-y-2">
            <div className="p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-medium rounded">admin</span>
              </div>
              <p className="text-sm text-stone-500">All pages - full platform access</p>
            </div>
            <div className="p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-medium rounded">marketing</span>
              </div>
              <p className="text-sm text-stone-500">Leads and Growth Playbook only</p>
            </div>
          </div>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Adding Custom Roles</h4>
          <ol className="list-decimal list-inside space-y-2 text-stone-600 dark:text-stone-400">
            <li>In the Role Permissions section, find the <strong>"Add New Role"</strong> input</li>
            <li>Enter the role name (lowercase, e.g., <code className="px-1 bg-stone-100 dark:bg-stone-800 rounded text-xs">support</code>)</li>
            <li>Click the <strong>+ button</strong> to add it</li>
            <li>Toggle the pages this role should access</li>
            <li>Save your changes</li>
          </ol>

          <div className="p-4 bg-violet-50 dark:bg-violet-900/20 rounded-xl border border-violet-200 dark:border-violet-800">
            <p className="text-violet-800 dark:text-violet-200 text-sm">
              <strong>Important:</strong> After adding a custom role, you'll need to assign it to users via Clerk's public metadata. The role name must match exactly (case-sensitive).
            </p>
          </div>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">How It Works</h4>
          <ul className="list-disc list-inside space-y-1 text-stone-600 dark:text-stone-400 ml-2">
            <li>Permissions are fetched from the database when the admin portal loads</li>
            <li>The sidebar only shows pages the current role has access to</li>
            <li>Routes are dynamically generated based on allowed pages</li>
            <li>Users without any configured pages see an "Access Denied" screen</li>
          </ul>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">API Endpoints</h4>
          <CodeBlock
            code={`# Get current permissions (public)
GET /admin/role-permissions

Response:
{
  "permissions": {
    "admin": {
      "pages": ["dashboard", "studios", ...],
      "defaultPage": "/"
    },
    "marketing": {
      "pages": ["leads", "playbook"],
      "defaultPage": "/leads"
    }
  }
}

# Update permissions (admin only)
PUT /admin/role-permissions
{
  "permissions": {
    "admin": {
      "pages": ["dashboard", "studios", ...],
      "defaultPage": "/"
    },
    "support": {
      "pages": ["users", "studios"],
      "defaultPage": "/users"
    }
  }
}`}
            id="role-permissions-api"
          />

          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
            <p className="text-amber-800 dark:text-amber-200 text-sm">
              <strong>Fallback:</strong> If the API fails to load permissions, default permissions are used (admin = all pages, marketing = leads + playbook).
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'studio-management',
      title: 'Studio Management',
      icon: Building2,
      color: 'bg-emerald-500',
      content: (
        <div className="space-y-4">
          <p className="text-stone-600 dark:text-stone-400">
            Monitor and manage all registered studios on the platform.
          </p>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200">Studio Data Isolation</h4>
          <p className="text-stone-600 dark:text-stone-400">
            Each studio's data is isolated by <code className="px-2 py-1 bg-stone-100 dark:bg-stone-800 rounded text-sm">user_id</code>.
            Studios cannot see each other's bookings, clients, or settings.
          </p>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Studio Setup Status</h4>
          <p className="text-stone-600 dark:text-stone-400">
            New studios must complete setup before accessing features:
          </p>
          <ul className="list-disc list-inside space-y-1 text-stone-600 dark:text-stone-400 ml-2">
            <li>Studio name and contact info</li>
            <li>At least one service</li>
            <li>At least one team member (therapist)</li>
          </ul>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Database Query Example</h4>
          <CodeBlock
            code={`SELECT * FROM studio_config
WHERE user_id = 'user_xxx'`}
            id="studio-query"
          />
        </div>
      ),
    },
    {
      id: 'studio-impersonation',
      title: 'Studio Impersonation',
      icon: Eye,
      color: 'bg-purple-500',
      content: (
        <div className="space-y-4">
          <p className="text-stone-600 dark:text-stone-400">
            Admins can view any studio's dashboard to provide support or verify issues.
          </p>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200">How to Use</h4>
          <ol className="list-decimal list-inside space-y-2 text-stone-600 dark:text-stone-400">
            <li>Go to <strong>Studios</strong> page</li>
            <li>Click the <strong>⋮</strong> menu on any studio row</li>
            <li>Select <strong>"View Dashboard"</strong></li>
            <li>A new tab opens with the studio's dashboard</li>
          </ol>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">What You'll See</h4>
          <ul className="list-disc list-inside space-y-1 text-stone-600 dark:text-stone-400 ml-2">
            <li>Purple banner indicating admin viewing mode</li>
            <li>Studio name displayed in the banner</li>
            <li>Full dashboard with all the studio's data</li>
            <li>"Close Tab" button to exit impersonation</li>
          </ul>

          <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
            <p className="text-purple-800 dark:text-purple-200 text-sm">
              <strong>Read-Only Mode:</strong> When impersonating, you view the studio's data but cannot make changes.
              This protects studio owners' data while allowing admins to troubleshoot.
            </p>
          </div>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Security</h4>
          <ul className="list-disc list-inside space-y-1 text-stone-600 dark:text-stone-400 ml-2">
            <li>Only users with <code className="px-1 bg-stone-100 dark:bg-stone-800 rounded text-xs">role: "admin"</code> can impersonate</li>
            <li>Impersonation tokens expire after <strong>1 hour</strong></li>
            <li>All API calls include <code className="px-1 bg-stone-100 dark:bg-stone-800 rounded text-xs">X-Impersonate-User</code> header</li>
            <li>API validates admin role before honoring impersonation requests</li>
          </ul>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">API Endpoint</h4>
          <CodeBlock
            code={`GET /admin/studios/:userId/impersonate

Response:
{
  "success": true,
  "studioName": "Zen Wellness Spa",
  "dashboardUrl": "https://serenetouch.app/dashboard?impersonate=...",
  "expiresAt": "2026-01-06T00:00:00.000Z"
}`}
            id="impersonate-api"
          />
        </div>
      ),
    },
    {
      id: 'bulk-operations',
      title: 'Bulk Operations',
      icon: Mail,
      color: 'bg-orange-500',
      content: (
        <div className="space-y-4">
          <p className="text-stone-600 dark:text-stone-400">
            Perform actions on multiple studios at once, such as sending announcements or changing plans.
          </p>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200">Bulk Email</h4>
          <ol className="list-decimal list-inside space-y-2 text-stone-600 dark:text-stone-400">
            <li>Go to <strong>Studios</strong> page</li>
            <li>Use checkboxes to select studios (or "Select All")</li>
            <li>Click <strong>"Send Email"</strong> in the toolbar</li>
            <li>Enter subject and message</li>
            <li>Click <strong>"Send Email"</strong> to deliver</li>
          </ol>

          <div className="p-4 bg-sage/10 rounded-xl border border-sage/30 mt-4">
            <p className="text-sage dark:text-sage text-sm">
              <strong>Tip:</strong> Emails are sent from <code className="px-1 bg-white/50 rounded">noreply@serenetouch.app</code>
              using the Resend provider. Each studio owner receives a personalized greeting.
            </p>
          </div>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Bulk Plan Changes</h4>
          <p className="text-stone-600 dark:text-stone-400">
            The bulk plan change endpoint is ready for when you need to migrate users between plans:
          </p>
          <CodeBlock
            code={`POST /admin/bulk/plan

Body:
{
  "userIds": ["user_xxx", "user_yyy"],
  "newPlan": "professional",
  "reason": "Promotional upgrade"
}

Response:
{
  "success": true,
  "message": "Queued plan changes for 2 of 2 studios"
}`}
            id="bulk-plan-api"
          />

          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
            <p className="text-amber-800 dark:text-amber-200 text-sm">
              <strong>Note:</strong> Plan changes require Stripe integration. The current implementation logs the request;
              actual subscription modifications should be done via Stripe Dashboard or extended API integration.
            </p>
          </div>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Available Actions</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <Mail className="w-5 h-5 text-orange-500" />
              <div>
                <p className="font-medium text-stone-700 dark:text-stone-300">Send Email</p>
                <p className="text-sm text-stone-500">Send announcements to selected studios</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <Eye className="w-5 h-5 text-purple-500" />
              <div>
                <p className="font-medium text-stone-700 dark:text-stone-300">View Dashboard</p>
                <p className="text-sm text-stone-500">Impersonate a single studio (per-row action)</p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'subscriptions',
      title: 'Subscription & Billing',
      icon: CreditCard,
      color: 'bg-indigo-500',
      content: (
        <div className="space-y-4">
          <p className="text-stone-600 dark:text-stone-400">
            SereneTouch uses Stripe for all billing operations.
          </p>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200">Subscription Plans</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <div>
                <p className="font-medium text-stone-700 dark:text-stone-300">Essential</p>
                <p className="text-sm text-stone-500">Basic features, 1 therapist</p>
              </div>
              <span className="font-semibold text-stone-800 dark:text-stone-200">$29/mo</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <div>
                <p className="font-medium text-stone-700 dark:text-stone-300">Professional</p>
                <p className="text-sm text-stone-500">All features, 5 therapists</p>
              </div>
              <span className="font-semibold text-stone-800 dark:text-stone-200">$79/mo</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <div>
                <p className="font-medium text-stone-700 dark:text-stone-300">Enterprise</p>
                <p className="text-sm text-stone-500">Unlimited, priority support</p>
              </div>
              <span className="font-semibold text-stone-800 dark:text-stone-200">$199/mo</span>
            </div>
          </div>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Trial Period</h4>
          <p className="text-stone-600 dark:text-stone-400">
            All new studios get a <strong>14-day free trial</strong> with full Professional features.
          </p>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Managing Subscriptions</h4>
          <p className="text-stone-600 dark:text-stone-400">
            Use the Stripe Dashboard to:
          </p>
          <ul className="list-disc list-inside space-y-1 text-stone-600 dark:text-stone-400 ml-2">
            <li>View payment history</li>
            <li>Issue refunds</li>
            <li>Cancel or pause subscriptions</li>
            <li>Update payment methods</li>
          </ul>
        </div>
      ),
    },
    {
      id: 'discounts',
      title: 'Discounts & Promotions',
      icon: Tag,
      color: 'bg-pink-500',
      content: (
        <div className="space-y-4">
          <p className="text-stone-600 dark:text-stone-400">
            Create and manage discount codes for early adopters and promotional campaigns. Discounts are synced with Stripe.
          </p>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200">Creating a Discount Code</h4>
          <ol className="list-decimal list-inside space-y-2 text-stone-600 dark:text-stone-400">
            <li>Go to <strong>Discounts</strong> page in the sidebar</li>
            <li>Click <strong>"Create Discount"</strong></li>
            <li>Enter a code name (e.g., EARLYBIRD50)</li>
            <li>Choose discount type (Percentage or Fixed Amount)</li>
            <li>Set the value (e.g., 50 for 50% off or $50 off)</li>
            <li>Configure duration and limits</li>
            <li>Click <strong>"Create Discount"</strong></li>
          </ol>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Discount Types</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-medium rounded">Percentage</span>
              <span className="text-stone-600 dark:text-stone-400">Discount by percentage (e.g., 25% off)</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium rounded">Fixed Amount</span>
              <span className="text-stone-600 dark:text-stone-400">Discount by dollar amount (e.g., $50 off)</span>
            </div>
          </div>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Duration Options</h4>
          <div className="space-y-2">
            <div className="p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <p className="font-medium text-stone-700 dark:text-stone-300">Once</p>
              <p className="text-sm text-stone-500">Applies to the first invoice only</p>
            </div>
            <div className="p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <p className="font-medium text-stone-700 dark:text-stone-300">Repeating</p>
              <p className="text-sm text-stone-500">Applies for a set number of months (e.g., 3 months)</p>
            </div>
            <div className="p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <p className="font-medium text-stone-700 dark:text-stone-300">Forever</p>
              <p className="text-sm text-stone-500">Applies to all future invoices while subscribed</p>
            </div>
          </div>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">How Users Apply Codes</h4>
          <p className="text-stone-600 dark:text-stone-400">
            Users enter promo codes directly on the Stripe checkout page. When they click "Subscribe" in the app,
            Stripe's hosted checkout includes a promo code field where they can enter codes like <code className="px-2 py-1 bg-stone-100 dark:bg-stone-800 rounded text-sm">EARLYBIRD50</code>.
          </p>

          <div className="p-4 bg-sage/10 rounded-xl border border-sage/30">
            <p className="text-sage dark:text-sage text-sm">
              <strong>Tip:</strong> Share discount codes via email campaigns, social media, or directly with early adopters.
              The code is validated and applied automatically by Stripe.
            </p>
          </div>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Manually Applying Discounts</h4>
          <p className="text-stone-600 dark:text-stone-400">
            For existing subscribers who missed a promotion, you can manually apply a discount:
          </p>
          <ol className="list-decimal list-inside space-y-2 text-stone-600 dark:text-stone-400">
            <li>Go to <strong>Subscriptions</strong> page</li>
            <li>Find the user's subscription</li>
            <li>Click the <strong>⋮</strong> menu and select <strong>"Apply Discount"</strong></li>
            <li>Choose the discount code to apply</li>
            <li>The discount will be applied to their next billing cycle</li>
          </ol>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Example Discount Codes</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200 dark:border-stone-700">
                  <th className="text-left py-2 text-stone-600 dark:text-stone-400 font-medium">Code</th>
                  <th className="text-left py-2 text-stone-600 dark:text-stone-400 font-medium">Type</th>
                  <th className="text-left py-2 text-stone-600 dark:text-stone-400 font-medium">Duration</th>
                  <th className="text-left py-2 text-stone-600 dark:text-stone-400 font-medium">Use Case</th>
                </tr>
              </thead>
              <tbody className="text-stone-700 dark:text-stone-300">
                <tr className="border-b border-stone-100 dark:border-stone-800">
                  <td className="py-2 font-mono">EARLYBIRD50</td>
                  <td className="py-2">50% off</td>
                  <td className="py-2">3 months</td>
                  <td className="py-2">Early adopters</td>
                </tr>
                <tr className="border-b border-stone-100 dark:border-stone-800">
                  <td className="py-2 font-mono">FOUNDER100</td>
                  <td className="py-2">100% off</td>
                  <td className="py-2">1 month</td>
                  <td className="py-2">First month free</td>
                </tr>
                <tr className="border-b border-stone-100 dark:border-stone-800">
                  <td className="py-2 font-mono">LAUNCH25</td>
                  <td className="py-2">25% off</td>
                  <td className="py-2">Forever</td>
                  <td className="py-2">Lifetime discount</td>
                </tr>
                <tr>
                  <td className="py-2 font-mono">SAVE50</td>
                  <td className="py-2">$50 off</td>
                  <td className="py-2">Once</td>
                  <td className="py-2">One-time savings</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">API Endpoints</h4>
          <CodeBlock
            code={`# List all discounts
GET /admin/discounts

# Create discount (creates in Stripe + local DB)
POST /admin/discounts
{
  "code": "EARLYBIRD50",
  "discount_type": "percent_off",
  "discount_value": 50,
  "duration": "repeating",
  "duration_in_months": 3
}

# Apply discount to existing subscription
POST /admin/discounts/apply
{
  "user_id": "user_xxx",
  "discount_code_id": "promo_xxx"
}`}
            id="discount-api"
          />

          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
            <p className="text-amber-800 dark:text-amber-200 text-sm">
              <strong>Note:</strong> Discounts are stored in both Stripe (as coupons/promotion codes) and locally for tracking.
              Stripe is the source of truth for actual discount application.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'in-app-notifications',
      title: 'In-App Notifications',
      icon: Bell,
      color: 'bg-amber-500',
      content: (
        <div className="space-y-4">
          <p className="text-stone-600 dark:text-stone-400">
            The admin portal has an in-app notification system that alerts you to important events. Click the bell icon in the header to view notifications.
          </p>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200">Notification Types</h4>
          <div className="space-y-2">
            <div className="flex items-start gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <UserPlus className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <p className="font-medium text-stone-700 dark:text-stone-300">Access Request</p>
                <p className="text-sm text-stone-500">When someone requests admin portal access or signs up pending approval</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <Building2 className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium text-stone-700 dark:text-stone-300">New Studio Signup</p>
                <p className="text-sm text-stone-500">When a new studio owner creates an account and starts a trial</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <CreditCard className="w-5 h-5 text-red-500 mt-0.5" />
              <div>
                <p className="font-medium text-stone-700 dark:text-stone-300">Payment Failed</p>
                <p className="text-sm text-stone-500">When a subscription payment fails (coming soon)</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <Zap className="w-5 h-5 text-purple-500 mt-0.5" />
              <div>
                <p className="font-medium text-stone-700 dark:text-stone-300">Hot Lead Added</p>
                <p className="text-sm text-stone-500">When a new lead with "hot" score is created in the CRM</p>
              </div>
            </div>
          </div>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Features</h4>
          <ul className="list-disc list-inside space-y-1 text-stone-600 dark:text-stone-400 ml-2">
            <li><strong>Badge count</strong> - Shows unread notification count on the bell icon</li>
            <li><strong>Auto-refresh</strong> - Polls for new notifications every 30 seconds</li>
            <li><strong>Click to navigate</strong> - Each notification links to the relevant page</li>
            <li><strong>Mark as read</strong> - Individual or bulk mark-all-read</li>
            <li><strong>Delete</strong> - Remove notifications you no longer need</li>
          </ul>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">API Endpoints</h4>
          <CodeBlock
            code={`# Get notifications (with pagination)
GET /admin/notifications?limit=20&offset=0

# Get unread count only (for badge)
GET /admin/notifications/unread-count

# Mark single notification as read
PATCH /admin/notifications/:id/read

# Mark all as read
POST /admin/notifications/mark-all-read

# Delete a notification (admin only)
DELETE /admin/notifications/:id

# Create notification (admin only)
POST /admin/notifications
{
  "type": "new_studio",
  "title": "New Studio Signup",
  "message": "John Doe just signed up for a trial.",
  "link": "/studios",
  "metadata": { "userId": "user_xxx" }
}`}
            id="in-app-notification-api"
          />

          <div className="p-4 bg-sage/10 rounded-xl border border-sage/30">
            <p className="text-sage dark:text-sage text-sm">
              <strong>Tip:</strong> Notifications complement Slack alerts. Slack is for immediate team awareness, while in-app notifications provide a persistent history you can review later.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'notifications',
      title: 'Client Notifications (SMS/Email)',
      icon: Mail,
      color: 'bg-teal-500',
      content: (
        <div className="space-y-4">
          <p className="text-stone-600 dark:text-stone-400">
            The client notification system uses swappable providers for SMS and Email to send booking reminders and confirmations.
          </p>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200">Provider Configuration</h4>
          <CodeBlock
            code={`# Environment Variables
ENABLE_NOTIFICATIONS=true
EMAIL_PROVIDER=resend
SMS_PROVIDER=twilio

# Twilio
TWILIO_ACCOUNT_SID=xxx
TWILIO_AUTH_TOKEN=xxx
TWILIO_FROM_NUMBER=+1xxx

# Resend
RESEND_API_KEY=re_xxx
RESEND_FROM_EMAIL=noreply@serenetouch.app`}
            id="notification-env"
          />

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Cost Per Message</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <span className="text-stone-600 dark:text-stone-400">SMS (Twilio, US)</span>
              <span className="font-mono text-stone-800 dark:text-stone-200">$0.0079</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <span className="text-stone-600 dark:text-stone-400">Email (Resend)</span>
              <span className="font-mono text-stone-800 dark:text-stone-200">$0.001</span>
            </div>
          </div>

          <div className="p-4 bg-sage/10 rounded-xl border border-sage/30">
            <p className="text-sage dark:text-sage text-sm">
              <strong>Tip:</strong> Costs are tracked in the <code className="px-1 bg-white/50 rounded">notification_costs</code> table
              for admin visibility. View cost summaries in the Analytics page.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'slack-notifications',
      title: 'Slack Notifications',
      icon: MessageSquare,
      color: 'bg-purple-500',
      content: (
        <div className="space-y-4">
          <p className="text-stone-600 dark:text-stone-400">
            Internal team notifications are sent to Slack for real-time visibility into lead activity and pipeline movement.
          </p>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200">Notification Types</h4>
          <div className="space-y-2">
            <div className="flex items-start gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <UserPlus className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium text-stone-700 dark:text-stone-300">New Lead Added</p>
                <p className="text-sm text-stone-500">Triggered when a new lead is created in the CRM</p>
                <p className="text-xs text-stone-400 mt-1">Includes: Contact name, studio, email, phone, source, score, pain point, added by</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <MessageSquare className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <p className="font-medium text-stone-700 dark:text-stone-300">Lead Status Changed</p>
                <p className="text-sm text-stone-500">Triggered when a lead moves through the pipeline</p>
                <p className="text-xs text-stone-400 mt-1">Shows: Lead name, old status → new status, changed by</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <span className="text-xl">📥</span>
              <div>
                <p className="font-medium text-stone-700 dark:text-stone-300">Bulk Import</p>
                <p className="text-sm text-stone-500">Triggered when leads are imported via CSV</p>
                <p className="text-xs text-stone-400 mt-1">Shows: Number imported, number failed</p>
              </div>
            </div>
          </div>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Status Change Emojis</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <div className="flex items-center gap-2 p-2 bg-stone-50 dark:bg-stone-800 rounded-lg">
              <span>🆕</span>
              <span className="text-sm text-stone-600 dark:text-stone-400">New</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-stone-50 dark:bg-stone-800 rounded-lg">
              <span>📞</span>
              <span className="text-sm text-stone-600 dark:text-stone-400">Contacted</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-stone-50 dark:bg-stone-800 rounded-lg">
              <span>📅</span>
              <span className="text-sm text-stone-600 dark:text-stone-400">Demo Scheduled</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-stone-50 dark:bg-stone-800 rounded-lg">
              <span>✅</span>
              <span className="text-sm text-stone-600 dark:text-stone-400">Demo Done</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-stone-50 dark:bg-stone-800 rounded-lg">
              <span>🎉</span>
              <span className="text-sm text-stone-600 dark:text-stone-400">Converted</span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-stone-50 dark:bg-stone-800 rounded-lg">
              <span>❌</span>
              <span className="text-sm text-stone-600 dark:text-stone-400">Lost</span>
            </div>
          </div>

          <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
            <p className="text-purple-800 dark:text-purple-200 text-sm">
              <strong>Note:</strong> When a lead is marked as <strong>Converted</strong>, a special celebration header (🎉 Lead Converted!) is shown.
            </p>
          </div>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Configuration</h4>
          <p className="text-stone-600 dark:text-stone-400">
            Slack notifications use a webhook URL configured in the API. The webhook is called asynchronously (fire-and-forget)
            so it doesn't slow down API responses.
          </p>
          <CodeBlock
            code={`// SlackProvider.ts
const SLACK_WEBHOOK_URL = 'https://hooks.slack.com/services/...';

// Usage in API routes
slackProvider.notifyNewLead({
  contactName: 'Jane Doe',
  studioName: 'Zen Wellness',
  email: 'jane@example.com',
  score: 'hot',
  createdByName: 'Admin User'
}).catch(err => console.error(err));`}
            id="slack-config"
          />

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Future Notifications</h4>
          <p className="text-stone-600 dark:text-stone-400">
            The SlackProvider is designed to be extended. Planned notifications include:
          </p>
          <ul className="list-disc list-inside space-y-1 text-stone-600 dark:text-stone-400 ml-2">
            <li>New studio signup</li>
            <li>Payment received</li>
            <li>Payment failed</li>
            <li>Trial expiring</li>
          </ul>
        </div>
      ),
    },
    {
      id: 'realtime',
      title: 'Realtime Updates',
      icon: Zap,
      color: 'bg-yellow-500',
      content: (
        <div className="space-y-4">
          <p className="text-stone-600 dark:text-stone-400">
            WebSocket connections allow instant schedule updates across multiple front-desk terminals.
          </p>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200">How It Works</h4>
          <ol className="list-decimal list-inside space-y-2 text-stone-600 dark:text-stone-400">
            <li>Each studio gets a Durable Object instance</li>
            <li>Terminals connect via WebSocket to their studio's instance</li>
            <li>When a booking changes, the API broadcasts to all connected clients</li>
            <li>All terminals update instantly without polling</li>
          </ol>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Enable Realtime</h4>
          <CodeBlock
            code={`# Environment Variables
ENABLE_REALTIME=true
REALTIME_PROVIDER=durable-objects`}
            id="realtime-env"
          />

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Estimated Cost</h4>
          <p className="text-stone-600 dark:text-stone-400">
            Cloudflare Durable Objects: <strong>~$5/month</strong> for typical usage.
          </p>
        </div>
      ),
    },
    {
      id: 'database',
      title: 'Database Operations',
      icon: Database,
      color: 'bg-cyan-500',
      content: (
        <div className="space-y-4">
          <p className="text-stone-600 dark:text-stone-400">
            SereneTouch uses Cloudflare D1 (SQLite) for data storage.
          </p>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200">Database Commands</h4>
          <CodeBlock
            code={`# Run from /api folder

# Reset database (clear user data)
npm run db:reset        # Production
npm run db:reset:dev    # Local

# Load demo data
npm run db:demo         # Production
npm run db:demo:dev     # Local

# Apply schema migrations
npm run db:migrate      # Production
npm run db:migrate:dev  # Local`}
            id="db-commands"
          />

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Database Tables</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {['studio_config', 'therapists', 'rooms', 'clients', 'services', 'bookings',
              'booking_therapists', 'booking_clients', 'scheduled_reminders', 'notification_costs',
              'discount_codes', 'discount_redemptions', 'admin_notifications', 'users', 'leads'].map(table => (
              <div key={table} className="px-3 py-2 bg-stone-50 dark:bg-stone-800 rounded-lg text-sm font-mono text-stone-600 dark:text-stone-400">
                {table}
              </div>
            ))}
          </div>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Direct Query</h4>
          <CodeBlock
            code={`npx wrangler d1 execute serenetouch-db \\
  --command "SELECT COUNT(*) FROM bookings"`}
            id="d1-query"
          />
        </div>
      ),
    },
    {
      id: 'deployment',
      title: 'Deployment',
      icon: Terminal,
      color: 'bg-stone-600',
      content: (
        <div className="space-y-4">
          <p className="text-stone-600 dark:text-stone-400">
            Each app deploys independently from within its folder.
          </p>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200">Deployment Commands</h4>
          <CodeBlock
            code={`# Client App (serenetouch.app)
cd client && npm run deploy

# Admin Portal (admin.serenetouch.app)
cd admin && npm run deploy

# API (api.serenetouch.app)
cd api && npm run deploy`}
            id="deploy-commands"
          />

          <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
            <p className="text-red-800 dark:text-red-200 text-sm">
              <strong>Important:</strong> Always deploy from within the correct folder.
              Running deploy from the root will fail.
            </p>
          </div>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Environment Variables</h4>
          <p className="text-stone-600 dark:text-stone-400">
            Set via Cloudflare dashboard or <code className="px-2 py-1 bg-stone-100 dark:bg-stone-800 rounded text-sm">wrangler secret put</code>:
          </p>
          <ul className="list-disc list-inside space-y-1 text-stone-600 dark:text-stone-400 ml-2">
            <li><code className="text-xs">CLERK_SECRET_KEY</code></li>
            <li><code className="text-xs">STRIPE_SECRET_KEY</code></li>
            <li><code className="text-xs">TWILIO_*</code> credentials</li>
            <li><code className="text-xs">RESEND_API_KEY</code></li>
          </ul>
        </div>
      ),
    },
    {
      id: 'api-keys',
      title: 'API Keys & Secrets',
      icon: Key,
      color: 'bg-red-500',
      content: (
        <div className="space-y-4">
          <p className="text-stone-600 dark:text-stone-400">
            Manage API keys and secrets securely through Cloudflare.
          </p>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200">Required Secrets</h4>
          <div className="space-y-2">
            <div className="p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <p className="font-medium text-stone-700 dark:text-stone-300">CLERK_SECRET_KEY</p>
              <p className="text-sm text-stone-500">From Clerk Dashboard → API Keys</p>
            </div>
            <div className="p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <p className="font-medium text-stone-700 dark:text-stone-300">STRIPE_SECRET_KEY</p>
              <p className="text-sm text-stone-500">From Stripe Dashboard → Developers → API Keys</p>
            </div>
            <div className="p-3 bg-stone-50 dark:bg-stone-800 rounded-xl">
              <p className="font-medium text-stone-700 dark:text-stone-300">STRIPE_WEBHOOK_SECRET</p>
              <p className="text-sm text-stone-500">From Stripe Dashboard → Webhooks</p>
            </div>
          </div>

          <h4 className="font-semibold text-stone-800 dark:text-stone-200 mt-6">Setting Secrets</h4>
          <CodeBlock
            code={`# Set a secret via Wrangler
cd api
wrangler secret put STRIPE_SECRET_KEY

# You'll be prompted to enter the value`}
            id="set-secret"
          />

          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
            <p className="text-amber-800 dark:text-amber-200 text-sm">
              <strong>Security:</strong> Never commit secrets to git. Use <code className="px-1 bg-white/50 rounded">.env.local</code> for local development
              and Cloudflare secrets for production.
            </p>
          </div>
        </div>
      ),
    },
  ];

  const toggleSection = (id: string) => {
    setExpandedSection(expandedSection === id ? null : id);
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-stone-800 dark:text-stone-100">Admin Guide</h1>
        <p className="text-stone-500 dark:text-stone-400 mt-1">
          Documentation and guides for managing the SereneTouch platform
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search documentation..."
          className="w-full px-4 py-3 pl-12 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
        />
        <BookOpen className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
      </div>

      {/* Guide Sections */}
      <div className="space-y-3">
        {sections.map((section) => (
          <div
            key={section.id}
            className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 overflow-hidden"
          >
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full flex items-center gap-4 p-4 hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors"
            >
              <div className={`w-10 h-10 ${section.color} rounded-xl flex items-center justify-center`}>
                <section.icon className="w-5 h-5 text-white" />
              </div>
              <span className="flex-1 text-left font-medium text-stone-800 dark:text-stone-100">
                {section.title}
              </span>
              {expandedSection === section.id ? (
                <ChevronDown className="w-5 h-5 text-stone-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-stone-400" />
              )}
            </button>

            {expandedSection === section.id && (
              <div className="px-4 pb-4 pt-0 border-t border-stone-100 dark:border-stone-800">
                <div className="pt-4">
                  {section.content}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Architecture Link */}
      <div className="bg-gradient-to-r from-sage/10 to-sage/5 rounded-2xl border border-sage/20 p-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-sage rounded-xl flex items-center justify-center">
            <Globe className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-stone-800 dark:text-stone-100">Visual Architecture</h3>
            <p className="text-sm text-stone-500 dark:text-stone-400">
              View the complete system architecture diagram
            </p>
          </div>
          <a
            href="/docs/ARCHITECTURE.html"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-xl font-medium hover:bg-sage/90 transition-colors"
          >
            View Diagram
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}
